"use client";

import React from "react";
import Link from "next/link";
import { useIsAdLanding } from "@/lib/hooks/useIsAdLanding";
import { HeroSection, type ButtonConfig } from "@/components/ui/HeroSection";
import { GradientCard } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";

/** Default (organic) hero buttons */
const DEFAULT_HERO_BUTTONS: ButtonConfig[] = [
  {
    text: "Book a Free Demo",
    href: "/book-a-demo",
    variant: "primary",
    target: "_self",
    arrow: "grow",
  },
  {
    text: "Start Free Trial",
    href: "https://console.pickcel.com/auth/register",
    variant: "outlinePrimary",
    target: "_self",
  },
];

/** Ad-landing hero buttons (no trial / login) */
const AD_HERO_BUTTONS: ButtonConfig[] = [
  {
    text: "Book a Demo",
    href: "/book-a-demo",
    variant: "primary",
    target: "_self",
    arrow: "grow",
  },
  {
    text: "Contact Us",
    href: "/company/contact",
    variant: "outlinePrimary",
    target: "_self",
  },
];

/**
 * Returns the correct set of hero buttons depending on whether the
 * current session originated from a paid-ad UTM link.
 */
export function useAdAwareHeroButtons(): ButtonConfig[] {
  const isAdLanding = useIsAdLanding();
  return isAdLanding ? AD_HERO_BUTTONS : DEFAULT_HERO_BUTTONS;
}

/** Default final-CTA buttons */
const DEFAULT_CTA_BUTTONS: ButtonConfig[] = [
  {
    text: "Book a Free Demo",
    href: "/book-a-demo",
    variant: "primary",
    target: "_self",
  },
  {
    text: "Start Free Trial",
    href: "https://console.pickcel.com/auth/register",
    variant: "outlinePrimary",
    target: "_blank",
  },
];

/** Ad-landing final-CTA buttons */
const AD_CTA_BUTTONS: ButtonConfig[] = [
  {
    text: "Contact Us",
    href: "/company/contact",
    variant: "primary",
    target: "_self",
  },
  {
    text: "Book a Demo",
    href: "/book-a-demo",
    variant: "outlinePrimary",
    target: "_self",
  },
];

/**
 * Returns the correct set of bottom-CTA buttons depending on
 * whether the current session originated from a paid-ad UTM link.
 */
export function useAdAwareCTAButtons(): ButtonConfig[] {
  const isAdLanding = useIsAdLanding();
  return isAdLanding ? AD_CTA_BUTTONS : DEFAULT_CTA_BUTTONS;
}

/**
 * A thin client wrapper around GradientCard that swaps its buttons
 * based on the ad-landing state. All other props are passed through.
 */
export function AdAwareGradientCTA({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  const buttons = useAdAwareCTAButtons();
  return (
    <GradientCard
      variant="cta-banner"
      centered={true}
      title={title}
      subtitle={subtitle}
      buttons={buttons}
    />
  );
}

type AdAwareSingleCtaProps = {
  defaultHref: string;
  defaultText: string;
  adHref: string;
  adText: string;
  className?: string;
  variant: React.ComponentProps<typeof NewButton>["variant"];
  size: React.ComponentProps<typeof NewButton>["size"];
  arrow?: React.ComponentProps<typeof NewButton>["arrow"];
  arrowColorClass?: string;
  disclaimer?: string;
  mixpanelId?: string;
  targetExternal?: "_blank" | "_self";
};

/**
 * Small, reusable CTA that swaps destination/text for ad (UTM) sessions.
 * UTM params are appended at click-time by `UTMAppendScript`.
 */
export function AdAwareSingleCtaButton({
  defaultHref,
  defaultText,
  adHref,
  adText,
  className,
  variant,
  size,
  arrow,
  arrowColorClass,
  disclaimer,
  mixpanelId,
  targetExternal = "_blank",
}: AdAwareSingleCtaProps) {
  const isAdLanding = useIsAdLanding();
  const href = isAdLanding ? adHref : defaultHref;
  const text = isAdLanding ? adText : defaultText;
  const isExternal = href.startsWith("http");

  if (isExternal) {
    return (
      <a
        href={href}
        target={targetExternal}
        rel="noopener noreferrer"
        data-mixpanel-id={mixpanelId}
      >
        <NewButton
          variant={variant}
          size={size}
          arrow={arrow}
          arrowColorClass={arrowColorClass}
          className={className}
          disclaimer={disclaimer}
        >
          {text}
        </NewButton>
      </a>
    );
  }

  return (
    <Link href={href} target="_self" data-mixpanel-id={mixpanelId}>
      <NewButton
        variant={variant}
        size={size}
        arrow={arrow}
        arrowColorClass={arrowColorClass}
        className={className}
        disclaimer={disclaimer}
      >
        {text}
      </NewButton>
    </Link>
  );
}
